import { FC, useMemo, useRef } from 'react';

import HotTable, { HotColumn } from '@handsontable/react';
import { IList } from 'core/API/listApi';
import { sortBy, toUpper, toLower } from 'lodash';
import { IRetailer } from 'shared/types';
import { PRODUCT_SCORE_CRITERIA } from 'pages/ListManagement/const';
import { CellValue, SourceData } from 'pages/ListManagement/types';
import { RatingAndReviewsThresholdsHeader, translateData, translateHeaders } from './helpers/HeadersList';
import { listValidator, numberValidator, retailerValidator } from '../Validators';
import Grid from '../Grid';
import { requireTitle } from './helpers/utils';
import RetailerRenderer from './helpers/CustomDropboxRenderer';
import { AutoCompleteRenderer } from './helpers/customRenderers';

export const RatingAndReviewsThresholds: FC<{
    list: IList;
    toolId: number;
    retailers: IRetailer[];
    categories: any[];
}> = ({ list, toolId, retailers, categories }) => {
    const hotRef = useRef<HotTable | null>(null);
    const translatedHeaders = translateHeaders(RatingAndReviewsThresholdsHeader);
    const translatedCriteria = translateData(PRODUCT_SCORE_CRITERIA);

    let categoriesList = categories.map((cat) => ({
        id: Number(cat.level_1),
        name: `${cat.level_name_1}`,
    }));

    const mapCriteria: any = {};
    const mapRetailer: any = {};
    const mapIDToCriteria: any = {};
    const retailersNameMap: Record<string, any> = {};
    const sortedRetailer = sortBy(retailers, [(o) => toUpper(o.name)]);
    retailers?.forEach((ret) => (retailersNameMap[toLower(ret.name)] = ret));
    retailers?.forEach((ret) => (mapRetailer[ret.id] = ret));

    const catsMap: Record<string, any> = {};
    const catsNameMap: Record<string, any> = {};
    categoriesList?.forEach((cat) => (catsMap[cat.id] = cat));
    categoriesList?.forEach((cat) => (catsNameMap[toLower(cat.name)] = cat));
    categoriesList = Object.values(catsMap);

    const sortedCats = sortBy(categoriesList, [(o) => toUpper(o.name)]);

    translatedCriteria.forEach((tC, i) => {
        mapCriteria[tC] = PRODUCT_SCORE_CRITERIA[i].split('.')[2];
        mapIDToCriteria[PRODUCT_SCORE_CRITERIA[i].split('.')[2]] = tC;
    });

    const beforeUpload = (listToUpload: SourceData) => {
        return listToUpload.map((data) => {
            const newRow = [...data];

            newRow[0] = retailersNameMap[toLower(newRow[0] as string)]?.id;
            newRow[1] = catsNameMap[toLower(newRow[1] as string)]?.id;
            newRow[4] = toLower(newRow[4] as string);

            return newRow;
        });
    };

    const beforeDownload = (listToDownload: CellValue[][]) => {
        const newData = listToDownload.map((row) => {
            const newRow = [...row];

            if (row[0]) {
                newRow[0] = mapRetailer[newRow[0] as string].name;
            }
            if (row[1]) {
                newRow[1] = catsMap[newRow[1] as string].name;
            }

            return newRow;
        });

        return newData;
    };

    const afterValidate = (listToValidate: IList) => {
        const newData = [...listToValidate.data].map((row) => {
            const newRow = [...row];

            if (row[1]) {
                newRow[1] = Number(newRow[1]);
            }
            if (row[2]) {
                newRow[2] = mapCriteria[newRow[2] as string];
            }
            if (row[4]) {
                newRow[4] = row[4] === 'true';
            }
            return newRow;
        });

        return { ...listToValidate, data: newData };
    };

    const formatList = (l: IList) => {
        const newData = l.data.map((data) => {
            const newRow = [...data];
            newRow[2] = mapIDToCriteria[newRow[2] as string];
            newRow[4] = newRow[4] + '';
            return newRow;
        });

        return {
            ...l,
            data: newData,
        };
    };

    const formatedList = useMemo(() => formatList(list), [list.data, list]);

    return (
        <Grid
            toolId={toolId}
            headers={RatingAndReviewsThresholdsHeader}
            list={formatedList}
            paramHotRef={hotRef}
            beforeUpload={beforeUpload}
            beforeDownload={beforeDownload}
            afterValidate={afterValidate}
        >
            <HotColumn
                validator={retailerValidator(retailers)}
                title={requireTitle(translatedHeaders[0])}
                readOnly={true}
            >
                <RetailerRenderer customList={sortedRetailer} hotInstance={hotRef} hot-renderer />
            </HotColumn>
            <HotColumn
                validator={retailerValidator(categoriesList)}
                title={requireTitle(translatedHeaders[1])}
                readOnly={true}
            >
                <RetailerRenderer customList={sortedCats} hotInstance={hotRef} hot-renderer />
            </HotColumn>
            <HotColumn
                validator={listValidator(translatedCriteria)}
                title={requireTitle(translatedHeaders[2])}
                source={translatedCriteria}
                type="autocomplete"
            >
                <AutoCompleteRenderer list={translatedCriteria} hot-renderer />
            </HotColumn>
            <HotColumn title={requireTitle(translatedHeaders[3])} type="numeric" validator={numberValidator()} />
            <HotColumn
                validator={listValidator(['true', 'false'])}
                title={requireTitle(translatedHeaders[4])}
                source={['true', 'false']}
                type="autocomplete"
            >
                <AutoCompleteRenderer list={['true', 'false']} hot-renderer />
            </HotColumn>
        </Grid>
    );
};
